<template>
  <div class="container">
    <div class="header columns m-0 px-0 is-vcentered">
      <router-link
        :to="
          isCreateMode
            ? { name: 'email_report_builder' }
            : { name: 'pdf_report_editor', params: { report_id: pdfReportID } }
        ">
        <b-button
          type="is-light"
          class="back-btn mr-3"
          icon-right="arrow-left" />
      </router-link>

      <div class="report-breadcrumbs">
        <div>
          <h4>Report Settings</h4>
          <h2>{{ reportData?.name || "Pdf Report" }}</h2>
        </div>
      </div>
      <div class="control-bar">
        <b-button
          :disabled="!isDirty"
          class="mr-5"
          @click="discardChanges">
          Discard Changes
        </b-button>

        <b-button
          type="is-primary"
          :disabled="!isDirty"
          @click="saveEdits">
          {{ isCreateMode ? "Create Report" : "Save Changes" }}
        </b-button>
      </div>
    </div>

    <b-loading
      v-model="isLoading"
      :is-full-page="false"
      :can-cancel="false" />

    <div
      v-if="!isLoading"
      class="mt-6">
      <div class="columns m-0">
        <div class="column is-4 mr-auto">
          <b-field
            label="Report Name"
            class="field">
            <ScopeLabelInput
              v-model="nameInput"
              :scopes="companyScopes"
              placeholder="Report Name" />
          </b-field>
          <b-field
            label="Report Default Time Range"
            class="field">
            <TimeRangeEditor
              v-model="timeRangeInput"
              :optional="false" />
          </b-field>
        </div>
        <div class="column is-6">
          <FilterConfigListEditor
            v-model="filtersInput"
            :default-time-range="reportData.default_time_range"
            default-time-range-label="Use report default"
            class="editor-inputs"
            @input="makeInputsDirty" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import TimeRangeEditor from '@/components/reports/config/modules/common/TimeRangeEditor'
  import FilterConfigListEditor from '@/components/reports/config/modules/common/FilterConfigListEditor.vue'
  import ScopeLabelInput from '@/components/pdf_report/ScopeLabelInput.vue'

  export default {
    name: 'PdfReportSettings',
    components: {
      FilterConfigListEditor,
      TimeRangeEditor,
      ScopeLabelInput,
    },
    props: {},
    data () {
      return {
        isCreateMode: true,
        reportData: {
          name: '',
          default_time_range: {
            key: 'wtd',
            name: 'Week to Date',
            type: 'CalendarPreset',
          },
          style: {
            is_a11y: false,
          },
          filters: [],
          modules: [],
        },
        pdfReportID: null,

        isLoading: true,
        isDirty: false,
        companyScopes: [],
      }
    },
    computed: {
      filtersInput: {
        get () {
          return this.reportData.filters
        },

        set (value) {
          this.reportData.filters = value
          this.isDirty = true
        },
      },

      nameInput: {
        get () {
          return this.reportData.name || ''
        },
        set (value) {
          this.reportData.name = value || ''
          this.isDirty = true
        },
      },

      timeRangeInput: {
        get () {
          return this.reportData.default_time_range
        },
        set (value) {
          this.reportData.default_time_range = {
            ...this.reportData.default_time_range,
            ...value,
          }
          this.isDirty = true
        },
      },
    },

    async mounted () {
      this.pdfReportID = this.$route.params.report_id

      const promises = [
        this.$store.dispatch('app/reportsConfig/refreshOptions'),
        this.loadCompanyScopes(),
      ]
      if (this.pdfReportID && this.pdfReportID !== 'new') {
        this.isCreateMode = false
        promises.push(this.loadPdfReportData())
      }
      await Promise.all(promises)
      this.isLoading = false
    },
    methods: {
      toggleFilters (val) {
        if (val) {
          this.reportData.filters = [
            {
              type: 'DimensionInFilter',
              dimension: 'location',
              values: [],
            },
          ]
        } else {
          this.reportData.filters = []
        }

        this.isDirty = true
      },
      async discardChanges () {
        this.isLoading = true
        await this.loadPdfReportData()
        this.isLoading = false
      },

      async loadPdfReportData () {
        const response = await this.$apis.pdfReports.getPdfReportData(
          this.pdfReportID
        )

        this.reportData = response

        await this.$nextTick()
        await this.$nextTick()
        await this.$nextTick()

        this.isDirty = false
      },

      async saveEdits () {
        this.isLoading = true
        const requestBody = this.reportData

        if (this.isCreateMode) {
          const response = await this.$apis.pdfReports.createPdfReport(
            requestBody
          )
          if (!response) {
            this.$buefy.toast.open({
              message: 'Backend Error, Report this to the devs',
              type: 'is-danger',
            })
          } else if (response.errors) {
            this.$buefy.toast.open({
              message: 'Error saving report, fix the errors and try again',
              type: 'is-warning',
            })
          } else {
            this.$router.push({
              name: 'pdf_report_editor',
              params: { report_id: response.key, },
            })
          }
        } else {
          const response = await this.$apis.pdfReports.updatePdfReport(
            this.pdfReportID,
            requestBody
          )
          if (!response) {
            this.$buefy.toast.open({
              message: 'Backend Error, Report this to the devs',
              type: 'is-danger',
            })
          } else if (response.errors) {
            this.$buefy.toast.open({
              message: 'Error saving report, fix the errors and try again',
              type: 'is-warning',
            })
          } else {
            this.isDirty = false
          }
        }

        this.isLoading = false
      },

      async loadCompanyScopes () {
        try {
          const scopes = await this.$apis.companyConfigs.listCompanyScopes()
          this.companyScopes = scopes || []
        } catch (e) {
          this.companyScopes = []
        }
      },

      makeInputsDirty () {
        this.isDirty = true
      },
    },
  }
</script>

<style lang="scss" scoped>
.report-breadcrumbs {
  display: flex;

  h2 {
    font-weight: 400;
    font-size: 20px;
    margin: 0;
  }

  h4 {
    font-weight: 400;
    font-size: 12px;
    margin: 0;
    color: $grayscale-3;
  }
}

.container {
  overflow-y: auto;
  position: relative;
  padding-bottom: 50px;
  padding: 20px;

  .header {
    border-bottom: $border-1;
    margin-top: 30px;

    padding-bottom: 20px;

    .back-btn {
      font-size: 24px;
      border-radius: 100%;
    }

    h2 {
      font-weight: 400;
      font-size: 22px;
    }

    .control-bar {
      display: flex;
      margin-left: auto;
      margin-right: 0;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
